import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Switch,
  Popover,
  Autocomplete,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Close, Save, Search, Add, Person } from "@mui/icons-material";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useTheme } from "@mui/material/styles";
import PictureAvatar from "components/PictureAvatar";

const ShiftCalendar = ({
  isModalOpen,
  setIsModalOpen,
  shifts: initialShifts,
  employees: allEmployees,
  onUpdate,
  job,
  isLoading = false,
}) => {
  const [currentView, setCurrentView] = useState("Week");
  const [selectedShift, setSelectedShift] = useState("All Shifts");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [draggedEmployee, setDraggedEmployee] = useState(null);
  const [dragOverShift, setDragOverShift] = useState(null);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [showAllStaff, setShowAllStaff] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isInitialLoad = useRef(true);
  const isUserChangingShift = useRef(false);

  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [modifiedShifts, setModifiedShifts] = useState(null);
  const [employeesLoading, setEmployeesLoading] = useState(false);

  // Position selection state
  const [positionPopupAnchor, setPositionPopupAnchor] = useState(null);
  const [selectedEmployeeForPosition, setSelectedEmployeeForPosition] = useState(null);
  const [selectedDateForPosition, setSelectedDateForPosition] = useState(null);
  const [selectedShiftForPosition, setSelectedShiftForPosition] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState("");

  const colorPalette = useMemo(
    () => [
      { bg: "#E8F5E9", text: "#388E3C" },
      { bg: "#F3E5F5", text: "#7B1FA2" },
      { bg: "#FFF3E0", text: "#F57C00" },
      { bg: "#FBE9E7", text: "#D84315" },
      { bg: "#E0F7FA", text: "#00838F" },
      { bg: "#F9FBE7", text: "#9E9D24" },
      { bg: "#EFEBE9", text: "#5D4037" },
      { bg: "#ECEFF1", text: "#455A64" },
      { bg: "#FCE4EC", text: "#C2185B" },
      { bg: "#E3F2FD", text: "#1976D2" },
    ],
    []
  );

  // Helper function to migrate roster data from old format to new format
  const migrateRosterData = (shift) => {
    if (!shift.defaultSchedule) return shift;

    const migratedShift = JSON.parse(JSON.stringify(shift));

    Object.keys(migratedShift.defaultSchedule).forEach((dayName) => {
      const daySchedule = migratedShift.defaultSchedule[dayName];
      if (daySchedule && daySchedule.roster && Array.isArray(daySchedule.roster)) {
        const rosterData = daySchedule.roster;

        // Check if it's already in new format
        if (
          rosterData.length > 0 &&
          typeof rosterData[0] === "object" &&
          rosterData[0].employeeId
        ) {
          return; // Already in new format
        }

        // Migrate from old format to new format
        if (rosterData.length > 0 && typeof rosterData[0] === "string") {
          const newRoster = [];

          // For each employee ID, create assignments for all dates in the shift range
          if (shift.shiftStartDate && shift.shiftEndDate) {
            const start = moment(shift.shiftStartDate);
            const end = moment(shift.shiftEndDate);

            for (let m = moment(start); m.isSameOrBefore(end, "day"); m.add(1, "days")) {
              if (m.format("dddd").toLowerCase() === dayName) {
                const dateKey = m.format("YYYY-MM-DD");
                rosterData.forEach((employeeId) => {
                  newRoster.push({ employeeId, date: dateKey });
                });
              }
            }
          }

          daySchedule.roster = newRoster;
        }
      }
    });

    return migratedShift;
  };

  useEffect(() => {
    if (initialShifts) {
      // Migrate roster data to new format if needed
      const migratedShifts = initialShifts.map((shift) => migrateRosterData(shift));
      setModifiedShifts(JSON.parse(JSON.stringify(migratedShifts)));

      const formatShiftTime = (schedule) => {
        if (!schedule) return "Time Varies";
        const firstScheduledDay = Object.values(schedule).find((day) => day.start && day.end);
        if (firstScheduledDay) {
          const startTime = moment(firstScheduledDay.start).format("h:mm A");
          const endTime = moment(firstScheduledDay.end).format("h:mm A");
          return `${startTime} - ${endTime}`;
        }
        return "Not Scheduled";
      };

      const formattedShifts = migratedShifts.map((shift, index) => ({
        id: shift.slug,
        name: shift.shiftName,
        time: formatShiftTime(shift.defaultSchedule),
        startDate: shift.shiftStartDate,
        endDate: shift.shiftEndDate,
        color: colorPalette[index % colorPalette.length].bg,
        textColor: colorPalette[index % colorPalette.length].text,
        shiftRoster: shift.shiftRoster
      }));
      setShifts(formattedShifts);

      const newAssignments = {};
      migratedShifts.forEach((shift) => {
        if (!shift.shiftStartDate || !shift.shiftEndDate) return;

        const start = moment(shift.shiftStartDate);
        const end = moment(shift.shiftEndDate);

        for (let m = moment(start); m.isSameOrBefore(end, "day"); m.add(1, "days")) {
          const dateKey = m.format("YYYY-MM-DD");
          const dayName = m.format("dddd").toLowerCase();

          const daySchedule = shift.defaultSchedule?.[dayName];
          if (daySchedule && daySchedule.start && daySchedule.end) {
            // Handle both old format (array of IDs) and new format (array of objects)
            const rosterData = daySchedule.roster || [];
            let assignedEmployeeIds = [];

            if (rosterData.length > 0) {
              // Check if it's the new format (objects with employeeId and date)
              if (typeof rosterData[0] === "object" && rosterData[0].employeeId) {
                // New format: filter by specific date
                assignedEmployeeIds = rosterData
                  .filter((item) => item.date === dateKey)
                  .map((item) => item.employeeId);
              } else {
                // Old format: array of employee IDs
                assignedEmployeeIds = rosterData;
              }
            }

            if (!newAssignments[dateKey]) {
              newAssignments[dateKey] = [];
            }

            const existingAssignment = newAssignments[dateKey].find(
              (a) => a.shiftId === shift.slug
            );
            if (!existingAssignment) {
              newAssignments[dateKey].push({
                shiftId: shift.slug,
                employees: assignedEmployeeIds,
              });
            }
          }
        }
      });
      setAssignments(newAssignments);
    }
  }, [initialShifts, colorPalette]);

  useEffect(() => {
    if (isLoading) {
      setEmployeesLoading(true);
    } else {
      setEmployeesLoading(false);
    }
  }, [isLoading]);

  useEffect(() => {
    if (allEmployees && allEmployees.length > 0) {
      const sortedEmployees = [...allEmployees].sort((a, b) => {
        const lastNameA = (a.lastName || "").trim();
        const lastNameB = (b.lastName || "").trim();
        const firstNameA = (a.firstName || "").trim();
        const firstNameB = (b.firstName || "").trim();

        // First compare by last name
        const lastNameComparison = lastNameA.localeCompare(lastNameB, 'en', { sensitivity: 'base' });
        if (lastNameComparison !== 0) return lastNameComparison;

        // If last names are equal, compare by first name
        return firstNameA.localeCompare(firstNameB, 'en', { sensitivity: 'base' });
      });

      const formattedEmployees = sortedEmployees.map((emp) => ({
        id: emp?._id,
        firstName: emp.firstName || "",
        lastName: emp.lastName || "",
        name: `${emp.firstName || ""} ${emp.lastName || ""}`.trim(),
        avatar: `${emp.firstName ? emp.firstName.charAt(0) : ""}${
          emp.lastName ? emp.lastName.charAt(0) : ""
        }`,
      }));

      setEmployees(formattedEmployees);
    } else if (allEmployees && allEmployees.length === 0) {
      setEmployeesLoading(false);
    }
  }, [allEmployees]);

  useEffect(() => {
    if (isModalOpen && isInitialLoad.current) {
      // If "All Shifts" is selected, show current week by default
      if (selectedShift === "All Shifts") {
        setCurrentDate(new Date());
        isInitialLoad.current = false;
      } else if (initialShifts && initialShifts.length > 0 && initialShifts[0].shiftStartDate) {
        setCurrentDate(new Date(initialShifts[0].shiftStartDate));
        isInitialLoad.current = false;
      } else if (job?.startDate) {
        setCurrentDate(new Date(job.startDate));
        isInitialLoad.current = false;
      }
    } else if (!isModalOpen) {
      isInitialLoad.current = true; // Reset when modal closes
    }
  }, [isModalOpen, initialShifts, job?.startDate, selectedShift]);

  // Handle date changes when selectedShift changes
  useEffect(() => {
    if (isModalOpen && isUserChangingShift.current) {
      // Only change date when user explicitly changes shift filter
      if (selectedShift === "All Shifts") {
        // When "All Shifts" is selected, show current week
        setCurrentDate(new Date());
      } else if (selectedShift !== "All Shifts" && shifts.length > 0) {
        // When a specific shift is selected, show that shift's start date
        const selectedShiftData = shifts.find(shift => shift.id === selectedShift);
        if (selectedShiftData && selectedShiftData.startDate) {
          setCurrentDate(new Date(selectedShiftData.startDate));
        }
      }
      // Reset the flag after handling the change
      isUserChangingShift.current = false;
    }
  }, [selectedShift, isModalOpen]); // Removed 'shifts' from dependencies to avoid unnecessary triggers

  // Refresh states when modal opens
  useEffect(() => {
    if (isModalOpen) {
      // Reset search and filters but preserve currentView and selectedShift
      setEmployeeSearch("");
      // Only reset showAllStaff on initial load, not every time modal opens
      if (isInitialLoad.current) {
        setShowAllStaff(true); // Reset to show all staff by default only on initial load
      }
      // Only reset selectedShift on initial load, not every time modal opens
      if (isInitialLoad.current) {
        isUserChangingShift.current = true;
        setSelectedShift("All Shifts");
      }
      setDraggedEmployee(null);
      setDragOverShift(null);

      // Refresh assignments and modifiedShifts if initialShifts changed
      if (initialShifts) {
        const migratedShifts = initialShifts.map((shift) => migrateRosterData(shift));
        setModifiedShifts(JSON.parse(JSON.stringify(migratedShifts)));

        const formatShiftTime = (schedule) => {
          if (!schedule) return "Time Varies";
          const firstScheduledDay = Object.values(schedule).find((day) => day.start && day.end);
          if (firstScheduledDay) {
            const startTime = moment(firstScheduledDay.start).format("h:mm A");
            const endTime = moment(firstScheduledDay.end).format("h:mm A");
            return `${startTime} - ${endTime}`;
          }
          return "Not Scheduled";
        };

        const formattedShifts = migratedShifts.map((shift, index) => ({
          id: shift.slug,
          name: shift.shiftName,
          time: formatShiftTime(shift.defaultSchedule),
          startDate: shift.shiftStartDate,
          endDate: shift.shiftEndDate,
          color: colorPalette[index % colorPalette.length].bg,
          textColor: colorPalette[index % colorPalette.length].text,
          shiftRoster: shift.shiftRoster
        }));
        setShifts(formattedShifts);

        const newAssignments = {};
        migratedShifts.forEach((shift) => {
          if (!shift.shiftStartDate || !shift.shiftEndDate) return;

          const start = moment(shift.shiftStartDate);
          const end = moment(shift.shiftEndDate);

          for (let m = moment(start); m.isSameOrBefore(end, "day"); m.add(1, "days")) {
            const dateKey = m.format("YYYY-MM-DD");
            const dayName = m.format("dddd").toLowerCase();

            const daySchedule = shift.defaultSchedule?.[dayName];
            if (daySchedule && daySchedule.start && daySchedule.end) {
              const rosterData = daySchedule.roster || [];
              let assignedEmployeeIds = [];

              if (rosterData.length > 0) {
                if (typeof rosterData[0] === "object" && rosterData[0].employeeId) {
                  assignedEmployeeIds = rosterData
                    .filter((item) => item.date === dateKey)
                    .map((item) => item.employeeId);
                } else {
                  assignedEmployeeIds = rosterData;
                }
              }

              if (!newAssignments[dateKey]) {
                newAssignments[dateKey] = [];
              }

              const existingAssignment = newAssignments[dateKey].find(
                (a) => a.shiftId === shift.slug
              );
              if (!existingAssignment) {
                newAssignments[dateKey].push({
                  shiftId: shift.slug,
                  employees: assignedEmployeeIds,
                });
              }
            }
          }
        });
        setAssignments(newAssignments);
      }
    }
  }, [isModalOpen, initialShifts, colorPalette]);

  // Cleanup states when modal closes
  useEffect(() => {
    if (!isModalOpen) {
      // Reset all states when modal closes but preserve currentView and selectedShift
      setEmployeeSearch("");
      // Don't reset showAllStaff when modal closes to preserve user's preference
      // Don't reset selectedShift when modal closes to preserve user's selection
      setDraggedEmployee(null);
      setDragOverShift(null);
      setAssignments({});
      setModifiedShifts(null);
      setShifts([]);
    }
  }, [isModalOpen]);

  const filteredEmployees = useMemo(
    () => {
      let filtered = employees.filter((emp) => emp.name.toLowerCase().includes(employeeSearch.toLowerCase()));
      
      // If "All Staff" is not selected, filter to show only assigned employees
      if (!showAllStaff) {
        if (selectedShift === "All Shifts") {
          // Show employees assigned to any shift (from both shiftRoster and assignments)
          const assignedEmployeeIds = new Set();
          
          // From shiftRoster
          shifts.forEach((shift) => {
            if (shift.shiftRoster && Array.isArray(shift.shiftRoster)) {
              shift.shiftRoster.forEach((rosterItem) => {
                if (rosterItem._id) {
                  assignedEmployeeIds.add(rosterItem._id);
                }
              });
            }
          });
          
          // From assignments
          Object.values(assignments).forEach((dailyAssignments) => {
            dailyAssignments.forEach((assignment) => {
              if (assignment.employees && Array.isArray(assignment.employees)) {
                assignment.employees.forEach((empId) => assignedEmployeeIds.add(empId));
              }
            });
          });
          
          console.log("All Shifts - Assigned Employee IDs from both sources:", Array.from(assignedEmployeeIds));
          filtered = filtered.filter((emp) => assignedEmployeeIds.has(emp.id));
        } else {
          // Show employees assigned to the specific selected shift
          const selectedShiftData = shifts.find((shift) => shift.id === selectedShift);
          const assignedEmployeeIds = new Set();
          
          // From shiftRoster for specific shift
          if (selectedShiftData && selectedShiftData.shiftRoster && Array.isArray(selectedShiftData.shiftRoster)) {
            selectedShiftData.shiftRoster.forEach((rosterItem) => {
              if (rosterItem._id) {
                assignedEmployeeIds.add(rosterItem._id);
              }
            });
          }
          
          // From assignments for specific shift
          Object.values(assignments).forEach((dailyAssignments) => {
            dailyAssignments.forEach((assignment) => {
              if (assignment.shiftId === selectedShift && assignment.employees && Array.isArray(assignment.employees)) {
                assignment.employees.forEach((empId) => assignedEmployeeIds.add(empId));
              }
            });
          });
          
          filtered = filtered.filter((emp) => assignedEmployeeIds.has(emp.id));
        }
      }
      
      return filtered;
    },
    [employees, employeeSearch, showAllStaff, selectedShift, shifts, assignments]
  );

  // Helper function to find original employee data
  const getOriginalEmployeeData = (employeeId) => {
    return allEmployees?.find((emp) => emp._id === employeeId);
  };

  // Helper functions
  const formatDate = (date) => {
    return moment(date).format("YYYY-MM-DD");
  };

  // Helper function to get all shifts an employee is assigned to on a specific date
  const getEmployeeAssignmentsForDate = (employeeId, dateKey) => {
    const employeeAssignments = [];

    modifiedShifts?.forEach((shift) => {
      if (!shift.shiftStartDate || !shift.shiftEndDate) return;

      const start = moment(shift.shiftStartDate);
      const end = moment(shift.shiftEndDate);

      // Check if the date falls within this shift's range
      const checkDate = moment(dateKey);
      if (checkDate.isBetween(start, end, "day", "[]")) {
        const dayName = checkDate.format("dddd").toLowerCase();
        const daySchedule = shift.defaultSchedule?.[dayName];

        if (daySchedule && daySchedule.roster) {
          const rosterData = daySchedule.roster;

          if (rosterData.length > 0) {
            let isAssigned = false;

            if (typeof rosterData[0] === "object" && rosterData[0].employeeId) {
              // New format: check if employee is assigned to this specific date
              isAssigned = rosterData.some(
                (item) => item.employeeId === employeeId && item.date === dateKey
              );
            } else {
              // Old format: check if employee is in the roster
              isAssigned = rosterData.includes(employeeId);
            }

            if (isAssigned) {
              employeeAssignments.push({
                shiftId: shift.slug,
                shiftName: shift.shiftName,
                daySchedule,
              });
            }
          }
        }
      }
    });

    return employeeAssignments;
  };

  // Function to check if an employee has time overlap with existing shifts on a given date
  const checkTimeOverlap = (employeeId, date, targetShiftId) => {
    const dateKey = formatDate(date);
    const dayName = moment(date).format("dddd").toLowerCase();

    // Helper to extract time part as HH:mm
    const getTime = (dt) => moment(dt).format("HH:mm");

    // Get the target shift's time for this day (time only)
    const targetShift = modifiedShifts?.find((s) => s.slug === targetShiftId);
    const targetDaySchedule = targetShift?.defaultSchedule?.[dayName];

    if (!targetDaySchedule || !targetDaySchedule.start || !targetDaySchedule.end) {
      return false; // No schedule for this day, no overlap
    }

    const targetStart = moment(getTime(targetDaySchedule.start), "HH:mm");
    const targetEnd = moment(getTime(targetDaySchedule.end), "HH:mm");

    // Check all other shifts the employee is assigned to on this date
    const employeeAssignments = getEmployeeAssignmentsForDate(employeeId, dateKey);

    // Check for overlap with any existing assignment
    const hasOverlap = employeeAssignments.some((assignment) => {
      if (assignment.shiftId === targetShiftId) {
        return false; // Skip the target shift itself
      }

      const shift = modifiedShifts?.find((s) => s.slug === assignment.shiftId);
      const daySchedule = shift?.defaultSchedule?.[dayName];

      if (daySchedule && daySchedule.start && daySchedule.end) {
        const existingStart = moment(getTime(daySchedule.start), "HH:mm");
        const existingEnd = moment(getTime(daySchedule.end), "HH:mm");

        // Overlap logic (inclusive)
        const overlap = targetStart.isBefore(existingEnd) && targetEnd.isAfter(existingStart);
        // Optionally log for debugging:
        // console.log('t4', overlap, targetStart.format(), existingEnd.format(), targetEnd.format(), existingStart.format());
        return overlap;
      }

      return false;
    });

    return hasOverlap;
  };

  // Function to check if current drag operation would cause overlap
  const wouldCauseOverlap = (date, targetShiftId) => {
    if (!draggedEmployee) return false;
    return checkTimeOverlap(draggedEmployee.id, date, targetShiftId);
  };

  // Position handling functions
  const handlePositionButtonClick = (event, employee, date, shiftId) => {
    setPositionPopupAnchor(event.currentTarget);
    setSelectedEmployeeForPosition(employee);
    setSelectedDateForPosition(date);
    // Store the full shift object for this cell
    const shiftObj = modifiedShifts?.find((s) => s.slug === shiftId);
    setSelectedShiftForPosition(shiftObj);
    setSelectedPosition("");
  };

  const handlePositionClose = () => {
    setPositionPopupAnchor(null);
    setSelectedEmployeeForPosition(null);
    setSelectedDateForPosition(null);
    setSelectedShiftForPosition(null);
    setSelectedPosition("");
  };

  // Get positions for the current shift (for popover)
  const getPopoverPositions = () => {
    if (!selectedShiftForPosition?.positions) return [];
    return selectedShiftForPosition.positions;
  };

  // Helper function to get position availability for a specific date
  const getPositionAvailabilityForDate = (positionName, date, shiftId) => {
    const shift = modifiedShifts?.find((s) => s.slug === shiftId);
    if (!shift?.positions) return { total: 0, assigned: 0, available: 0 };

    const position = shift.positions.find((pos) => pos.positionName === positionName);
    if (!position) return { total: 0, assigned: 0, available: 0 };

    const total = parseInt(position.numberPositions, 10) || 0;
    const dateKey = formatDate(date);
    const dayName = moment(date).format("dddd").toLowerCase();
    const daySchedule = shift.defaultSchedule?.[dayName];
    
    let assigned = 0;
    if (daySchedule?.roster) {
      // Count only employees who are actually assigned to this position on this specific date
      assigned = daySchedule.roster.filter(
        (item) => item.date === dateKey && 
                   item.assignedPosition === positionName &&
                   item.employeeId // Make sure employeeId exists
      ).length;
    }

    const available = Math.max(0, total - assigned);
    return { total, assigned, available };
  };

  // Helper function to check for position conflicts on a specific day
  const checkPositionConflict = (positionName, date, shiftId, employeeId) => {
    const shift = modifiedShifts?.find((s) => s.slug === shiftId);
    const dateKey = formatDate(date);
    const dayName = moment(date).format("dddd").toLowerCase();
    const daySchedule = shift?.defaultSchedule?.[dayName];
    
    if (!daySchedule?.roster) return { hasConflict: false, conflictingEmployee: null };
    
    // Get position details to check availability
    const position = shift?.positions?.find((pos) => pos.positionName === positionName);
    if (!position) return { hasConflict: false, conflictingEmployee: null };
    
    const totalPositions = parseInt(position.numberPositions, 10) || 0;
    
    // Count how many employees are currently assigned to this position on this date
    const currentAssignments = daySchedule.roster.filter(
      (item) => item.date === dateKey && 
                 item.assignedPosition === positionName &&
                 item.employeeId // Make sure employeeId exists
    );
    
    const currentAssignmentCount = currentAssignments.length;
    
    // Check if this employee is already assigned to this position
    const isAlreadyAssigned = currentAssignments.some(
      (item) => item.employeeId === employeeId
    );
    
    // If employee is already assigned, no conflict
    if (isAlreadyAssigned) {
      return { hasConflict: false, conflictingEmployee: null };
    }
    
    // If we've reached the maximum capacity, it's a conflict
    if (currentAssignmentCount >= totalPositions) {
      const conflictingEmployee = employees.find((e) => e.id === currentAssignments[0]?.employeeId);
      return { 
        hasConflict: true, 
        conflictingEmployee: conflictingEmployee?.name || 'Unknown Employee',
        reason: 'capacity_full'
      };
    }
    
    return { hasConflict: false, conflictingEmployee: null };
  };

  // Helper function to check if position assignment is allowed
  const canAssignToPosition = (positionName, date, shiftId, currentEmployeeId = null) => {
    const { available, assigned } = getPositionAvailabilityForDate(positionName, date, shiftId);
    
    // If this employee is already assigned to this position on this date, allow it
    if (currentEmployeeId) {
      const shift = modifiedShifts?.find((s) => s.slug === shiftId);
      const dateKey = formatDate(date);
      const dayName = moment(date).format("dddd").toLowerCase();
      const daySchedule = shift?.defaultSchedule?.[dayName];
      
      if (daySchedule?.roster) {
        const existingAssignment = daySchedule.roster.find(
          (item) => item.employeeId === currentEmployeeId && 
                     item.date === dateKey && 
                     item.assignedPosition === positionName
        );
        if (existingAssignment) {
          return true; // Allow if employee is already assigned to this position
        }
      }
    }
    
    return available > 0;
  };

  // Get the current assigned position for the employee/date/shift (from roster)
  const getPopoverAssignedPosition = () => {
    if (!selectedEmployeeForPosition || !selectedDateForPosition || !selectedShiftForPosition) return "";
    const dateKey = formatDate(selectedDateForPosition);
    const dayName = moment(selectedDateForPosition).format("dddd").toLowerCase();
    const daySchedule = selectedShiftForPosition.defaultSchedule?.[dayName];
    if (!daySchedule?.roster) return "";
    const entry = daySchedule.roster.find(
      (item) => item.employeeId === selectedEmployeeForPosition.id && item.date === dateKey
    );
    return entry?.assignedPosition || "";
  };

  // Set assignedPosition in the correct roster entry
  const handlePositionSelect = (positionName) => {
    if (!selectedEmployeeForPosition || !selectedDateForPosition || !selectedShiftForPosition) return;
    const dateKey = formatDate(selectedDateForPosition);
    const dayName = moment(selectedDateForPosition).format("dddd").toLowerCase();
    const shiftSlug = selectedShiftForPosition.slug;
    const currentPosition = getPopoverAssignedPosition();

    // If clearing position (positionName is empty or null)
    if (!positionName) {
      // Update modifiedShifts state to clear the position
      const updatedModifiedShifts = modifiedShifts.map((shift) => {
        if (shift.slug === shiftSlug) {
          const newShift = JSON.parse(JSON.stringify(shift));
          const daySchedule = newShift.defaultSchedule[dayName];
          if (daySchedule && daySchedule.roster) {
            const entry = daySchedule.roster.find(
              (item) => item.employeeId === selectedEmployeeForPosition.id && item.date === dateKey
            );
            if (entry) {
              entry.assignedPosition = "";
            }
          }
          return newShift;
        }
        return shift;
      });
      setModifiedShifts(updatedModifiedShifts);
      enqueueSnackbar(
        `Position cleared for ${selectedEmployeeForPosition.name}`,
        { variant: "success" }
      );
      handlePositionClose();
      return;
    }

    // If changing to a different position
    if (positionName !== currentPosition) {
      // First check for position conflicts (different employees with same position on same day)
      const conflictCheck = checkPositionConflict(
        positionName, 
        selectedDateForPosition, 
        shiftSlug, 
        selectedEmployeeForPosition.id
      );
      
      if (conflictCheck.hasConflict) {
        const { total, assigned } = getPositionAvailabilityForDate(
          positionName, 
          selectedDateForPosition, 
          shiftSlug
        );
        enqueueSnackbar(
          `Cannot assign ${selectedEmployeeForPosition.name} to ${positionName}. Position is at maximum capacity (${assigned}/${total} assigned).`,
          { variant: "error" }
        );
        return;
      }
      
      // Then check general availability
      const canAssign = canAssignToPosition(
        positionName, 
        selectedDateForPosition, 
        shiftSlug, 
        selectedEmployeeForPosition.id
      );
      
      if (!canAssign) {
        const { total, assigned } = getPositionAvailabilityForDate(
          positionName, 
          selectedDateForPosition, 
          shiftSlug
        );
        enqueueSnackbar(
          `Cannot assign to ${positionName}. All ${total} positions are already filled (${assigned}/${total} assigned).`,
          { variant: "error" }
        );
        return;
      }
    }

    // Update modifiedShifts state
    const updatedModifiedShifts = modifiedShifts.map((shift) => {
      if (shift.slug === shiftSlug) {
        const newShift = JSON.parse(JSON.stringify(shift));
        const daySchedule = newShift.defaultSchedule[dayName];
        if (daySchedule && daySchedule.roster) {
          const entry = daySchedule.roster.find(
            (item) => item.employeeId === selectedEmployeeForPosition.id && item.date === dateKey
          );
          if (entry) {
            entry.assignedPosition = positionName;
          }
        }
        return newShift;
      }
      return shift;
    });
    setModifiedShifts(updatedModifiedShifts);
    enqueueSnackbar(
      `Position "${positionName}" assigned to ${selectedEmployeeForPosition.name}`,
      { variant: "success" }
    );
    handlePositionClose();
  };

  // For cell display: get assignedPosition from the correct shift's roster
  const getEmployeePosition = (employeeId, date, shiftId) => {
    const dateKey = formatDate(date);
    const dayName = moment(date).format("dddd").toLowerCase();
    const shift = modifiedShifts?.find((s) => s.slug === shiftId);
    if (!shift?.defaultSchedule?.[dayName]?.roster) return "";
    const entry = shift.defaultSchedule[dayName].roster.find(
      (item) => item.employeeId === employeeId && item.date === dateKey
    );
    return entry?.assignedPosition || "";
  };

  // Helper function to get position availability summary for a shift on a specific date
  const getPositionAvailabilitySummary = (shiftId, date) => {
    const shift = modifiedShifts?.find((s) => s.slug === shiftId);
    if (!shift?.positions || shift.positions.length === 0) return null;

    const dateKey = formatDate(date);
    const dayName = moment(date).format("dddd").toLowerCase();
    const daySchedule = shift.defaultSchedule?.[dayName];
    
    const summary = shift.positions.map(position => {
      const total = parseInt(position.numberPositions, 10) || 0;
      let assigned = 0;
      
      if (daySchedule?.roster) {
        assigned = daySchedule.roster.filter(
          (item) => item.date === dateKey && item.assignedPosition === position.positionName
        ).length;
      }
      
      return {
        positionName: position.positionName,
        total,
        assigned,
        available: total - assigned
      };
    });

    return summary;
  };

  const getWeekDays = () => {
    const start = new Date(currentDate);

    if (currentView === "Day") {
      return [new Date(currentDate)];
    }

    // Start of week (Monday) - get the Monday of the current week
    const dayOfWeek = start.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to Monday-based
    start.setDate(start.getDate() - daysFromMonday);
    
    const days = [];
    for (let i = 0; i < 7; i += 1) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getMonthDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    
    // Start from Monday instead of Sunday
    const dayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to Monday-based
    startDate.setDate(firstDay.getDate() - daysFromMonday);

    const weeks = [];
    const currentDateInLoop = new Date(startDate);

    while (currentDateInLoop <= lastDay || weeks.length < 5) {
      const week = [];
      for (let i = 0; i < 7; i += 1) {
        week.push(new Date(currentDateInLoop));
        currentDateInLoop.setDate(currentDateInLoop.getDate() + 1);
      }
      weeks.push(week);
      if (currentDateInLoop.getMonth() > month && weeks.length >= 4) break;
    }

    return weeks;
  };

  const getDayName = (date) => {
    return date.toLocaleDateString("en-US", { weekday: "long" }).toUpperCase();
  };

  const getDayDate = (date) => {
    return date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" });
  };

  const getShiftsForDate = (date) => {
    const dateKey = formatDate(date);
    const dailyAssignments = assignments[dateKey] || [];

    const scheduledShifts = dailyAssignments
      .map((assignment) => {
        const shiftDetails = shifts.find((s) => s.id === assignment.shiftId);
        if (!shiftDetails) return null;

        // Get the day name for this date
        const dayName = moment(date).format("dddd").toLowerCase();

        // Find the shift in modifiedShifts to get the roster data
        const shiftData = modifiedShifts?.find((s) => s.slug === assignment.shiftId);
        let employeesForThisDate = assignment.employees || [];

        if (shiftData?.defaultSchedule?.[dayName]?.roster) {
          const rosterData = shiftData.defaultSchedule[dayName].roster;
          if (rosterData.length > 0) {
            // Check if it's the new format (objects with employeeId and date)
            if (typeof rosterData[0] === "object" && rosterData[0].employeeId) {
              // New format: filter by specific date
              employeesForThisDate = rosterData
                .filter((item) => item.date === dateKey)
                .map((item) => item.employeeId);
            } else {
              // Old format: array of employee IDs
              employeesForThisDate = rosterData;
            }
          }
        }

        return {
          ...shiftDetails,
          employees: employeesForThisDate,
        };
      })
      .filter(Boolean);

    if (selectedShift !== "All Shifts") {
      return scheduledShifts.filter((s) => s.id === selectedShift);
    }

    return scheduledShifts;
  };

  const handleDragStart = (e, employee) => {
    setDraggedEmployee(employee);
    e.dataTransfer.effectAllowed = "move";

    // Get original employee data for profile image
    const originalEmp = getOriginalEmployeeData(employee.id);

    // Create a custom drag image to prevent multiple avatar display
    const dragImage = document.createElement("div");
    dragImage.style.cssText = `
      position: absolute;
      top: -1000px;
      left: -1000px;
      background: white;
      border: 2px solid #1976d2;
      border-radius: 24px;
      padding: 8px 16px;
      display: flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: #1976d2;
      white-space: nowrap;
      z-index: 1000;
    `;

    // Create avatar (image or initials)
    const avatar = document.createElement("div");
    avatar.style.cssText = `
      width: 24px;
      height: 24px;
      border-radius: 50%;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: 600;
      background: #1976d2;
      color: white;
    `;

    // Check if employee has a profile image
    if (originalEmp?.profileImg) {
      const img = document.createElement("img");
      img.src = originalEmp.profileImg;
      img.style.cssText = `
        width: 100%;
        height: 100%;
        object-fit: cover;
        border: none;
      `;
      img.onerror = () => {
        // If image fails to load, fall back to initials
        avatar.innerHTML = employee.avatar;
      };
      avatar.appendChild(img);
    } else {
      // Use initials if no profile image
      avatar.textContent = employee.avatar;
    }

    // Create name text
    const nameText = document.createElement("span");
    nameText.textContent = employee.name;

    dragImage.appendChild(avatar);
    dragImage.appendChild(nameText);
    document.body.appendChild(dragImage);

    // Set the custom drag image
    e.dataTransfer.setDragImage(dragImage, 20, 20);

    // Clean up the temporary element after a short delay
    setTimeout(() => {
      if (document.body.contains(dragImage)) {
        document.body.removeChild(dragImage);
      }
    }, 0);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (shiftId, date) => {
    setDragOverShift(`${shiftId}-${formatDate(date)}`);
  };

  const handleDragLeave = () => {
    setDragOverShift(null);
  };

  const handleDrop = (e, date, targetShiftId) => {
    e.preventDefault();
    if (!draggedEmployee || !targetShiftId) return;

    const { id: employeeId } = draggedEmployee;
    const dayName = moment(date).format("dddd").toLowerCase();
    const dateKey = formatDate(date);

    // Check for time overlap before allowing assignment
    const hasTimeOverlap = checkTimeOverlap(employeeId, date, targetShiftId);
    if (hasTimeOverlap) {
      enqueueSnackbar(
        `Cannot assign ${
          draggedEmployee.name
        } to this shift due to time overlap with existing assignment on ${moment(date).format(
          "MMM DD, YYYY"
        )}`,
        { variant: "error" }
      );
      setDraggedEmployee(null);
      setDragOverShift(null);
      return;
    }

    // 1. Update modifiedShifts state
    const updatedModifiedShifts = modifiedShifts.map((shift) => {
      if (shift.slug === targetShiftId) {
        const newShift = JSON.parse(JSON.stringify(shift)); // Deep copy to avoid mutation issues
        const daySchedule = newShift.defaultSchedule[dayName];

        if (daySchedule) {
          if (!daySchedule.roster) {
            daySchedule.roster = [];
          }

          // Check if employee is already assigned to this specific date
          const isAlreadyAssigned = daySchedule.roster.some(
            (item) => item.employeeId === employeeId && item.date === dateKey
          );

          if (!isAlreadyAssigned) {
            daySchedule.roster.push({ employeeId, date: dateKey });
          }
        }

        const roster = newShift.shiftRoster || [];
        if (!roster.some((emp) => emp._id === employeeId)) {
          const employeeDetails = allEmployees.find((emp) => emp._id === employeeId);
          if (employeeDetails) {
            roster.push({
              _id: employeeDetails._id,
              fullName: `${employeeDetails.firstName} ${employeeDetails.lastName}`,
              firstName: employeeDetails.firstName,
              lastName: employeeDetails.lastName,
              email: employeeDetails.email,
              payRate: employeeDetails.payRate || "",
              billRate: employeeDetails.billRate || "",
            });
          }
        }
        newShift.shiftRoster = roster;
        return newShift;
      }
      return shift;
    });
    setModifiedShifts(updatedModifiedShifts);

    // 2. Update assignments state for immediate UI feedback for the specific date only
    setAssignments((prevAssignments) => {
      const newAssignments = { ...prevAssignments };

      if (!newAssignments[dateKey]) {
        newAssignments[dateKey] = [];
      }

      let assignment = newAssignments[dateKey].find((a) => a.shiftId === targetShiftId);
      if (!assignment) {
        assignment = { shiftId: targetShiftId, employees: [], employeePositions: {} };
        newAssignments[dateKey].push(assignment);
      }

      if (!assignment.employees.includes(employeeId)) {
        assignment.employees.push(employeeId);
      }

      // Initialize employeePositions if it doesn't exist
      if (!assignment.employeePositions) {
        assignment.employeePositions = {};
      }

      return newAssignments;
    });

    setDraggedEmployee(null);
    setDragOverShift(null);
  };

  const handleDeleteAssignment = (date, employeeId, shiftId) => {
    const dayName = moment(date).format("dddd").toLowerCase();
    const dateKey = formatDate(date);

    // 1. Update modifiedShifts
    const updatedModifiedShifts = modifiedShifts.map((shift) => {
      if (shift.slug === shiftId) {
        const newShift = JSON.parse(JSON.stringify(shift));
        const daySchedule = newShift.defaultSchedule[dayName];
        if (daySchedule && daySchedule.roster) {
          // Remove the specific employee assignment for this date
          daySchedule.roster = daySchedule.roster.filter(
            (item) => !(item.employeeId === employeeId && item.date === dateKey)
          );
        }
        return newShift;
      }
      return shift;
    });
    setModifiedShifts(updatedModifiedShifts);

    // 2. Update assignments state for UI
    setAssignments((prevAssignments) => {
      const newAssignments = { ...prevAssignments };

      if (newAssignments[dateKey]) {
        newAssignments[dateKey] = newAssignments[dateKey].map((assignment) => {
          if (assignment.shiftId === shiftId) {
            const updatedAssignment = {
              ...assignment,
              employees: assignment.employees.filter((id) => id !== employeeId),
            };
            
            // Clean up position assignment for this employee
            if (assignment.employeePositions) {
              const { [employeeId]: removedPosition, ...remainingPositions } = assignment.employeePositions;
              updatedAssignment.employeePositions = remainingPositions;
            }
            
            return updatedAssignment;
          }
          return assignment;
        });
      }

      return newAssignments;
    });
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);

    if (currentView === "Day") {
      newDate.setDate(newDate.getDate() + direction);
    } else if (currentView === "Week") {
      newDate.setDate(newDate.getDate() + direction * 7);
    } else if (currentView === "Month") {
      newDate.setMonth(newDate.getMonth() + direction);
    }

    setCurrentDate(newDate);
  };

  const navigateMonthWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + direction * 7);
    setCurrentDate(newDate);
  };

  const renderShiftCard = (shift, date, isDayView = false, isMonthSummary = false) => {
    const dateKey = formatDate(date);
    const dragKey = `${shift.id}-${dateKey}`;
    const isDragOver = dragOverShift === dragKey;
    const wouldOverlap = wouldCauseOverlap(date, shift.id);

    const assignedEmployees = shift.employees || [];
    const isEmpty = assignedEmployees.length === 0;

    // Helper function to get the appropriate text for empty shifts
    const getEmptyShiftText = () => {
      if (wouldOverlap) return "Can't Assign";
      if (isDayView) return "Drop to assign";
      return "Unassigned";
    };

    const baseStyle = {
      backgroundColor: shift.color,
      color: shift.textColor,
      borderRadius: (() => {
        if (isDayView) return 2;
        if (isMonthSummary) return 1;
        return 1;
      })(),
      padding: (() => {
        if (isDayView) return "12px 16px";
        if (isMonthSummary) return "8px 10px";
        return "4px 8px";
      })(),
      fontSize: (() => {
        if (isDayView) return "0.875rem";
        if (isMonthSummary) return "0.65rem";
        return "0.75rem";
      })(),
      position: "relative",
      cursor: "pointer",
      transition: "all 0.3s ease",
      minHeight: (() => {
        if (isDayView) return "60px";
        if (isMonthSummary) return "60px";
        return "40px";
      })(),
      minWidth: isDayView ? "180px" : "auto",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      marginBottom: (() => {
        if (isDayView) return 0;
        if (isMonthSummary) return 0.4;
        return 0.5;
      })(),
      border: (() => {
        if (wouldOverlap) return "2px solid #f44336";
        if (isDragOver) return `2px solid ${shift.textColor}`;
        return "2px solid transparent";
      })(),
      transform: (() => {
        if (wouldOverlap) return "scale(1.05)";
        if (isDragOver) return "scale(1.05)";
        return "scale(1)";
      })(),
      boxShadow: (() => {
        if (wouldOverlap) return "0 6px 20px rgba(244, 67, 54, 0.3)";
        if (isDragOver) return "0 6px 20px rgba(0,0,0,0.25)";
        if (isMonthSummary) return "0 1px 3px rgba(0,0,0,0.1)";
        return "0 2px 8px rgba(0,0,0,0.1)";
      })(),
      "&:hover": {
        transform: "scale(1.02)",
        boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
      },
    };

    if (isEmpty) {
      baseStyle.border = (() => {
        if (wouldOverlap) return "2px dashed #f44336";
        if (isDragOver) return `2px dashed ${shift.textColor}`;
        return "2px dashed rgba(255,255,255,0.3)";
      })();
      baseStyle.opacity = 0.7;
    }

    const card = (
      <Box
        sx={baseStyle}
        onDragOver={handleDragOver}
        onDragEnter={() => handleDragEnter(shift.id, date)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, date, shift.id)}
      >
        {wouldOverlap && (
          <Typography
            variant="caption"
            color="white"
            sx={{
              position: "absolute",
              top: -2,
              left: "95%",
              transform: "translateX(-95%)",
              backgroundColor: "rgb(243, 92, 81)",
              color: "white",
              padding: "2px 6px",
              borderRadius: 1,
              fontSize: "0.6rem",
              fontWeight: 600,
              zIndex: 2,
              whiteSpace: "nowrap",
            }}
          >
            TIME OVERLAP
          </Typography>
        )}
        {isEmpty ? (
          <>
            <Typography
              variant="body1"
              sx={{
                fontSize: (() => {
                  if (isDayView) return "1rem";
                  if (isMonthSummary) return "0.6rem";
                  return "0.7rem";
                })(),
                fontWeight: 600,
                mb: isDayView ? 1 : 0.5,
                color: wouldOverlap ? "#f44336" : "inherit",
                mt: wouldOverlap ? 1 : 0,
              }}
            >
              {getEmptyShiftText()}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: (() => {
                  if (isDayView) return "0.9rem";
                  if (isMonthSummary) return "0.55rem";
                  return "0.65rem";
                })(),
                color: wouldOverlap ? "#f44336" : "inherit",
              }}
            >
              {shift.time}
            </Typography>
          </>
        ) : (
          <>
            <Typography
              variant="body2"
              sx={{
                fontSize: (() => {
                  if (isDayView) return "0.9rem";
                  if (isMonthSummary) return "0.55rem";
                  return "0.65rem";
                })(),
                fontWeight: 600,
                mb: isDayView ? 1 : 0.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: wouldOverlap ? "#f44336" : "inherit",
              }}
            >
              {shift.time}
            </Typography>

            {/* Position Availability Summary */}
            {!isMonthSummary && (() => {
              const positionSummary = getPositionAvailabilitySummary(shift.id, date);
              if (!positionSummary || positionSummary.length === 0) return null;
              
              return (
                <Box sx={{ mb: isDayView ? 1 : 0.5 }}>
                  {positionSummary.map((pos) => (
                    <Chip
                      key={pos.positionName}
                      label={`${pos.positionName}: ${pos.assigned}/${pos.total}`}
                      size="small"
                      sx={{
                        fontSize: "0.6rem",
                        height: 16,
                        backgroundColor: pos.available > 0 ? "rgba(255,255,255,0.3)" : "rgba(255,0,0,0.3)",
                        color: "inherit",
                        mr: 0.5,
                        mb: 0.5,
                        "& .MuiChip-label": {
                          px: 1,
                        },
                      }}
                    />
                  ))}
                </Box>
              );
            })()}

            {isMonthSummary ? (
              <>
                <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", flexWrap: "wrap" }}>
                  {assignedEmployees.slice(0, 2).map((empId) => {
                    const emp = employees.find((e) => e.id === empId);
                    const originalEmp = getOriginalEmployeeData(empId);
                    return emp ? (
                      <PictureAvatar
                        key={empId}
                        image={originalEmp?.profileImg}
                        firstName={originalEmp?.firstName}
                        lastName={originalEmp?.lastName}
                        userId={empId}
                        size="xs"
                        disableHover
                      />
                    ) : null;
                  })}
                  {assignedEmployees.length > 2 && (
                    <Avatar
                      sx={{
                        width: 14,
                        height: 14,
                        fontSize: "0.5rem",
                        backgroundColor: "rgba(255,255,255,0.3)",
                        color: "inherit",
                      }}
                    >
                      +{assignedEmployees.length - 2}
                    </Avatar>
                  )}
                </Box>
              </>
            ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", justifyContent: "center" }}>
                {assignedEmployees.map((empId) => {
                  const emp = employees.find((e) => e.id === empId);
                  const position = getEmployeePosition(empId, date, shift.id);
                  return emp ? (
                    <Chip
                      key={empId}
                      label={position ? `${emp.name} (${position})` : emp.name}
                      size="small"
                      onDelete={() => handleDeleteAssignment(date, empId, shift.id)}
                      sx={{
                        backgroundColor: "rgba(255,255,255,0.2)",
                        color: "inherit",
                        fontSize: "0.75rem",
                        "& .MuiChip-deleteIcon": {
                          color: "inherit",
                          fontSize: "0.8rem",
                        },
                      }}
                    />
                  ) : null;
                })}
              </Box>
            )}
          </>
        )}
      </Box>
    );

    if (isMonthSummary && assignedEmployees.length > 0) {
      const tooltipTitle = assignedEmployees
        .map((empId) => {
          const emp = employees.find((e) => e.id === empId);
          const position = getEmployeePosition(empId, date, shift.id);
          if (!emp) return null;
          return position ? `${emp.name} (${position})` : emp.name;
        })
        .filter(Boolean)
        .join(", ");

      return (
        <Tooltip key={`${shift.id}-${dateKey}`} title={tooltipTitle} placement="top" arrow>
          {card}
        </Tooltip>
      );
    }

    return React.cloneElement(card, { key: `${shift.id}-${dateKey}` });
  };

  const renderEmployeeCell = (date, employee, isDayView = false) => {
    const dateKey = formatDate(date);
    const shiftsForDate = getShiftsForDate(date);
    const cellWidth = isDayView ? "400px" : "120px";
    const assignedShifts = shiftsForDate.filter((shift) => shift.employees.includes(employee.id));

    if (assignedShifts.length === 0) {
      return (
        <TableCell
          key={`${employee.id}-${dateKey}`}
          sx={{
            height: isDayView ? 150 : 80,
            width: cellWidth,
            minWidth: cellWidth,
            border: "1px solid #e0e0e0",
            backgroundColor: "#f8f8f8",
            opacity: 0.7,
          }}
        />
      );
    }

    return (
      <TableCell
        key={`${employee.id}-${dateKey}`}
        sx={{
          height: isDayView ? "auto" : 80,
          width: cellWidth,
          minWidth: cellWidth,
          border: "1px solid #e0e0e0",
          position: "relative",
          padding: 1,
        }}
      >
        {isDayView ? (
          <Box
            sx={{ display: "flex", flexDirection: "row", gap: 1, flexWrap: "wrap", minHeight: 150 }}
          >
            {assignedShifts.map((shift) => {
              const currentPosition = getEmployeePosition(employee.id, date, shift.id);
              return (
                <Box
                  key={`${employee.id}-${dateKey}-${shift.id}`}
                  sx={{
                    backgroundColor: shift.color,
                    color: shift.textColor,
                    borderRadius: 2,
                    padding: "12px 16px",
                    fontSize: "0.875rem",
                    position: "relative",
                    minHeight: "60px",
                    minWidth: "180px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "1 1 auto",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.02)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      "& .delete-button": {
                        opacity: 1,
                      },
                      "& .position-button": {
                        opacity: 1,
                      },
                    },
                  }}
                >
                  <Typography variant="body1" sx={{ fontSize: "1rem", fontWeight: 600, mb: 1 }}>
                    {shift.time}
                  </Typography>
                  
                  {currentPosition && (
                    <Chip
                      label={currentPosition}
                      size="small"
                      sx={{
                        backgroundColor: "rgba(255,255,255,0.2)",
                        color: "inherit",
                        fontSize: "0.7rem",
                        mb: 1,
                      }}
                    />
                  )}

                  <Box sx={{ display: "flex", gap: 0.5, position: "absolute", top: 8, right: 8 }}>
                    <IconButton
                      className="position-button"
                      size="small"
                      onClick={(e) => handlePositionButtonClick(e, employee, date, shift.id)}
                      sx={{
                        opacity: 0,
                        transition: "opacity 0.2s",
                        backgroundColor: "#2196f3",
                        color: "white",
                        width: 24,
                        height: 24,
                        "&:hover": {
                          backgroundColor: "#1976d2",
                        },
                      }}
                    >
                      <Person sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton
                      className="delete-button"
                      size="small"
                      onClick={() => handleDeleteAssignment(date, employee.id, shift.id)}
                      sx={{
                        opacity: 0,
                        transition: "opacity 0.2s",
                        backgroundColor: "#f44336",
                        color: "white",
                        width: 24,
                        height: 24,
                        "&:hover": {
                          backgroundColor: "#d32f2f",
                        },
                      }}
                    >
                      <Close sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          assignedShifts.map((shift) => {
            const currentPosition = getEmployeePosition(employee.id, date, shift.id);
            return (
              <Box
                key={`${employee.id}-${dateKey}-${shift.id}`}
                sx={{
                  backgroundColor: shift.color,
                  color: shift.textColor,
                  borderRadius: 1,
                  padding: "4px 8px",
                  marginBottom: assignedShifts.length > 1 ? 0.5 : 0,
                  fontSize: "0.75rem",
                  position: "relative",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.02)",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    "& .delete-button": {
                      opacity: 1,
                    },
                    "& .position-button": {
                      opacity: 1,
                    },
                  },
                }}
              >
                <Typography variant="caption" sx={{ fontSize: "0.7rem", fontWeight: 500, mb: 0.5, display: "block" }}>
                  {shift.time}
                </Typography>
                
                {currentPosition && (
                  <Typography variant="caption" sx={{ fontSize: "0.6rem", opacity: 0.8, display: "block" }}>
                    {currentPosition}
                  </Typography>
                )}

                <Box sx={{ display: "flex", gap: 0.5, position: "absolute", top: -4, right: -4 }}>
                  <IconButton
                    className="position-button"
                    size="small"
                    onClick={(e) => handlePositionButtonClick(e, employee, date, shift.id)}
                    sx={{
                      opacity: 0,
                      transition: "opacity 0.2s",
                      backgroundColor: "#2196f3",
                      color: "white",
                      width: 16,
                      height: 16,
                      "&:hover": {
                        backgroundColor: "#1976d2",
                      },
                    }}
                  >
                    <Person sx={{ fontSize: 10 }} />
                  </IconButton>
                  <IconButton
                    className="delete-button"
                    size="small"
                    onClick={() => handleDeleteAssignment(date, employee.id, shift.id)}
                    sx={{
                      opacity: 0,
                      transition: "opacity 0.2s",
                      backgroundColor: "#f44336",
                      color: "white",
                      width: 16,
                      height: 16,
                      "&:hover": {
                        backgroundColor: "#d32f2f",
                      },
                    }}
                  >
                    <Close sx={{ fontSize: 10 }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })
        )}
      </TableCell>
    );
  };

  const renderShiftsCell = (date, isDayView = false) => {
    const shiftsForDate = getShiftsForDate(date);
    const cellWidth = isDayView ? "400px" : "120px";

    return (
      <TableCell
        key={`shifts-${formatDate(date)}`}
        sx={{
          height: isDayView ? "auto" : 80,
          width: cellWidth,
          minWidth: cellWidth,
          border: "1px solid #e0e0e0",
          position: "relative",
          padding: 1,
          backgroundColor: "rgb(245, 250, 254)",
        }}
      >
        <Box
          sx={{
            maxHeight: "260px",
            overflowY: "auto",
            overflowX: "hidden",
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a8a8a8",
            },
          }}
        >
          {isDayView ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                flexWrap: "wrap",
                minHeight: 150,
              }}
            >
              {shiftsForDate.map((shift) => renderShiftCard(shift, date, true))}
            </Box>
          ) : (
            shiftsForDate.map((shift) => renderShiftCard(shift, date, false))
          )}
        </Box>
      </TableCell>
    );
  };

  const weekDays = getWeekDays();
  const monthWeeks = getMonthDays();

  const getDateRangeText = () => {
    if (currentView === "Day") {
      return currentDate.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }

    if (currentView === "Week") {
      return `${weekDays[0].toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })} - ${weekDays[6].toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })}, ${weekDays[0].getFullYear()}`;
    }

    return currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  const handleSaveChanges = async () => {
    try {
      await onUpdate({ ...job, shifts: modifiedShifts });
    } catch (error) {
      enqueueSnackbar("Failed to save changes.", { variant: "error" });
      console.error("Error saving changes:", error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: { width: "95vw", height: "90vh", maxWidth: "none" },
        }}
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 2 }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Shift Assignment Calendar
          </Typography>
          <IconButton onClick={() => setIsModalOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: 3 }}>
          {/* Controls */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              mb: 3,
              flexWrap: "wrap",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={selectedShift}
                  onChange={(e) => {
                    isUserChangingShift.current = true;
                    setSelectedShift(e.target.value);
                  }}
                  displayEmpty
                  sx={{
                    minHeight: 38,
                    backgroundColor: "white",
                    borderRadius: 2,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1976d2",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1976d2",
                    },
                  }}
                >
                  <MenuItem value="All Shifts">All Shifts</MenuItem>
                  {shifts.map((shift) => (
                    <MenuItem key={shift.id} value={shift.id}>
                      {shift.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                variant="outlined"
                value={employeeSearch}
                onChange={(e) => setEmployeeSearch(e.target.value)}
                placeholder="Search Employees..."
                sx={{
                  minWidth: 240,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1976d2",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1976d2",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl component="fieldset" size="small">
                <FormControlLabel
                  control={
                    <Switch
                      checked={showAllStaff}
                      onChange={(e) => setShowAllStaff(e.target.checked)}
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#1976d2",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: "#1976d2",
                        },
                      }}
                    />
                  }
                  label="All Staff"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#666",
                    },
                  }}
                />
              </FormControl>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <ToggleButtonGroup
                value={currentView}
                exclusive
                onChange={(e, newView) => newView && setCurrentView(newView)}
                sx={{
                  backgroundColor: "white",
                  borderRadius: 2,
                  "& .MuiToggleButton-root": {
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px !important",
                    margin: "0 2px",
                    padding: "8px 16px",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    textTransform: "none",
                    color: "#666",
                    "&.Mui-selected": {
                      backgroundColor: "#1976d2",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "#1565c0",
                      },
                    },
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  },
                }}
              >
                <ToggleButton value="Day">Day</ToggleButton>
                <ToggleButton value="Week">Week</ToggleButton>
                <ToggleButton value="Month">Month</ToggleButton>
              </ToggleButtonGroup>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton
                  onClick={() => navigateWeek(-1)}
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <ChevronLeft />
                </IconButton>
                <Typography
                  variant="body2"
                  sx={{ minWidth: 200, textAlign: "center", fontWeight: 500 }}
                >
                  {getDateRangeText()}
                </Typography>
                <IconButton
                  onClick={() => navigateWeek(1)}
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <ChevronRight />
                </IconButton>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mb: 3,
              flexWrap: "wrap",
            }}
          >
            {/* Shift Legend */}
            <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
              {shifts.map((shift) => (
                <Chip
                  key={shift.id}
                  label={
                    shift.startDate && shift.endDate
                      ? `${shift.name} (${moment(shift.startDate).format("YYYY/MM/DD")} - ${moment(
                          shift.endDate
                        ).format("YYYY/MM/DD")})`
                      : shift.name
                  }
                  size="small"
                  sx={{
                    backgroundColor: shift.color,
                    color: shift.textColor,
                    fontSize: "0.8rem",
                    p: 2,
                  }}
                />
              ))}
            </Box>
            {/* Month Week Navigation */}
            {currentView === "Month" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  mb: 2,
                }}
              >
                <IconButton
                  onClick={() => navigateMonthWeek(-1)}
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <ChevronLeft />
                </IconButton>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, minWidth: 150, textAlign: "center" }}
                >
                  Week Navigation
                </Typography>
                <IconButton
                  onClick={() => navigateMonthWeek(1)}
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <ChevronRight />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Calendar Table */}
          {currentView === "Month" ? (
            // Month View
            <Box>
              {employeesLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 400,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <CircularProgress size={60} />
                </Box>
              ) : (
                <>
                  <TableContainer component={Paper} sx={{ border: "1px solid #e0e0e0", mb: 3, maxHeight: 850 }}>
                    <Table size="small" sx={{ minWidth: 800, tableLayout: "fixed" }}>
                      <TableBody>
                        <TableRow sx={{ height: 80, position: "sticky", top: 0, zIndex: 1 }}>
                          <TableCell
                            sx={{
                              backgroundColor: "#f5f5f5",
                              fontWeight: 600,
                              width: 140,
                              borderBottom: "1px solid #e0e0e0",
                            }}
                          >
                            EMPLOYEE
                          </TableCell>
                          {weekDays.map((day) => (
                            <TableCell
                              key={formatDate(day)}
                              align="center"
                              sx={{
                                backgroundColor: "#f5f5f5",
                                fontWeight: 600,
                                width: 120,
                                borderBottom: "1px solid #e0e0e0",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ fontSize: "0.75rem", fontWeight: 600 }}
                              >
                                {getDayName(day)}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ fontSize: "0.7rem", display: "block" }}
                              >
                                {getDayDate(day)}
                              </Typography>
                            </TableCell>
                          ))}
                        </TableRow>

                        {/* Shifts Row */}
                        <TableRow sx={{ position: "sticky", top: currentView === "Day" ? 60 : 80, zIndex: 1}}>
                          <TableCell
                            sx={{
                              backgroundColor: "rgb(245, 250, 254)",
                              borderRight: "2px solid #e0e0e0",
                              width: "140px",
                              minWidth: "140px",
                              maxWidth: "140px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.9rem",
                                  fontWeight: 600,
                                  color: "#2196f3",
                                }}
                              >
                                SHIFTS
                              </Typography>
                            </Box>
                          </TableCell>
                          {weekDays.map((day) => renderShiftsCell(day))}
                        </TableRow>

                        {filteredEmployees.map((employee) => (
                          <TableRow key={employee.id}>
                            <TableCell
                              sx={{
                                backgroundColor: "#fafafa",
                                cursor: "move",
                                borderRight: "2px solid #e0e0e0",
                                width: "140px",
                                minWidth: "140px",
                                maxWidth: "140px",
                              }}
                              draggable
                              onDragStart={(e) => handleDragStart(e, employee)}
                              onDragEnd={() => {
                                setDraggedEmployee(null);
                                setDragOverShift(null);
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <PictureAvatar
                                  image={getOriginalEmployeeData(employee.id)?.profileImg}
                                  firstName={getOriginalEmployeeData(employee.id)?.firstName}
                                  lastName={getOriginalEmployeeData(employee.id)?.lastName}
                                  userId={employee.id}
                                  size="md"
                                  disableHover
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "0.8rem",
                                    fontWeight: 400,
                                  }}
                                >
                                  {employee.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            {weekDays.map((day) => renderEmployeeCell(day, employee))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Monthly Summary */}
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Monthly Summary For: {getDateRangeText()}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 0.2 }}>
                    {/* Employee List Column */}
                    <Paper sx={{ border: "1px solid #e0e0e0", width: 200, borderRadius: 2 }}>
                      <Box
                        sx={{
                          backgroundColor: "#f5f5f5",
                          p: 1,
                          borderBottom: "1px solid #e0e0e0",
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          EMPLOYEES
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          maxHeight: "810px", // Fixed height for employee list
                          overflowY: "auto",
                          overflowX: "hidden",
                          "&::-webkit-scrollbar": {
                            width: "6px",
                          },
                          "&::-webkit-scrollbar-track": {
                            background: "#f1f1f1",
                            borderRadius: "10px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background: "#c1c1c1",
                            borderRadius: "10px",
                          },
                          "&::-webkit-scrollbar-thumb:hover": {
                            background: "#a8a8a8",
                          },
                        }}
                      >
                        {filteredEmployees.map((employee) => (
                          <Box
                            key={employee.id}
                            sx={{
                              p: 1.5,
                              borderBottom: "1px solid #f0f0f0",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              cursor: "move",
                              minHeight: "55px",
                              backgroundColor: "#fafafa",
                              transition: "background-color 0.2s",
                              "&:hover": {
                                backgroundColor: "#f0f0f0",
                              },
                              "&:last-child": {
                                borderBottom: "none",
                              },
                            }}
                            draggable
                            onDragStart={(e) => handleDragStart(e, employee)}
                            onDragEnd={() => {
                              setDraggedEmployee(null);
                              setDragOverShift(null);
                            }}
                          >
                            <PictureAvatar
                              image={getOriginalEmployeeData(employee.id)?.profileImg}
                              firstName={getOriginalEmployeeData(employee.id)?.firstName}
                              lastName={getOriginalEmployeeData(employee.id)?.lastName}
                              userId={employee.id}
                              size="sm"
                              disableHover
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.8rem",
                                fontWeight: 400,
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {employee.name}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Paper>

                    <TableContainer component={Paper} sx={{ border: "1px solid #e0e0e0" }}>
                      <Table size="small" sx={{ minWidth: 800 }}>
                        <TableBody>
                          {monthWeeks.map((week, weekIndex) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <TableRow key={`week-${weekIndex}-${week[0]?.getTime() || weekIndex}`}>
                              <TableCell
                                sx={{
                                  backgroundColor: "#fafafa",
                                  fontWeight: 600,
                                  textAlign: "center",
                                  width: "80px",
                                  minWidth: "80px",
                                  maxWidth: "80px",
                                }}
                              >
                                Week {weekIndex + 1}
                              </TableCell>
                              {week.map((day) => {
                                const shiftsForDay = getShiftsForDate(day);
                                const isCurrentMonth = day.getMonth() === currentDate.getMonth();

                                return (
                                  <TableCell
                                    key={`${day.getTime()}-${day.toDateString()}`}
                                    align="center"
                                    sx={{
                                      height: "180px",
                                      width: "140px",
                                      minWidth: "140px",
                                      maxWidth: "140px",
                                      position: "relative",
                                      backgroundColor: (() => {
                                        if (!isCurrentMonth) return "#f9f9f9";
                                        const dayShifts = getShiftsForDate(day);
                                        return dayShifts.length === 0 ? "#f8f8f8" : "white";
                                      })(),
                                      opacity: isCurrentMonth ? 1 : 0.5,
                                      border: "1px solid #e0e0e0",
                                      padding: 1,
                                      verticalAlign: "top",
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                      {day.getDate()}
                                    </Typography>
                                    {shiftsForDay.length > 0 && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 0.5,
                                          height: "140px",
                                          overflowY: "auto",
                                          overflowX: "hidden",
                                          "&::-webkit-scrollbar": {
                                            width: "4px",
                                          },
                                          "&::-webkit-scrollbar-track": {
                                            background: "#f1f1f1",
                                            borderRadius: "10px",
                                          },
                                          "&::-webkit-scrollbar-thumb": {
                                            background: "#c1c1c1",
                                            borderRadius: "10px",
                                          },
                                          "&::-webkit-scrollbar-thumb:hover": {
                                            background: "#a8a8a8",
                                          },
                                        }}
                                      >
                                        {shiftsForDay.map((shift) =>
                                          renderShiftCard(shift, day, false, true)
                                        )}
                                      </Box>
                                    )}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </>
              )}
            </Box>
          ) : (
            // Day and Week View
            <>
              {employeesLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 400,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <CircularProgress size={60} />
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ border: "1px solid #e0e0e0", maxHeight: 850 }}>
                  <Table
                    size="small"
                    sx={{ minWidth: 800, tableLayout: "fixed", position: "relative" }}
                  >
                    <TableBody>
                      <TableRow
                        sx={{
                          height: currentView === "Day" ? 60 : 80,
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        <TableCell
                          sx={{
                            backgroundColor: "#f5f5f5",
                            fontWeight: 600,
                            width: currentView === "Day" ? 80 : 140,
                            borderBottom: "1px solid #e0e0e0",
                          }}
                        >
                          EMPLOYEE
                        </TableCell>
                        {weekDays.map((day) => (
                          <TableCell
                            key={formatDate(day)}
                            align="center"
                            sx={{
                              backgroundColor: "#f5f5f5",
                              fontWeight: 600,
                              width: currentView === "Day" ? 400 : 120,
                              borderBottom: "1px solid #e0e0e0",
                            }}
                          >
                            <Typography variant="caption" sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                              {currentView === "Day"
                                ? day.toLocaleDateString("en-US", { weekday: "long" }).toUpperCase()
                                : getDayName(day)}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: "0.7rem", display: "block" }}>
                              {currentView === "Day"
                                ? day.toLocaleDateString("en-US", {
                                    month: "2-digit",
                                    day: "2-digit",
                                    year: "numeric",
                                  })
                                : getDayDate(day)}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>

                      {/* Shifts Row */}
                      <TableRow
                        sx={{ position: "sticky", top: currentView === "Day" ? 60 : 80, zIndex: 1 }}
                      >
                        <TableCell
                          sx={{
                            backgroundColor: "#f0f8ff",
                            borderRight: "2px solid #e0e0e0",
                            width: currentView === "Day" ? "80px" : "140px",
                            minWidth: currentView === "Day" ? "80px" : "140px",
                            maxWidth: currentView === "Day" ? "80px" : "140px",
                            height: currentView === "Day" ? 60 : "auto",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: currentView === "Day" ? "0.9rem" : "0.9rem",
                                fontWeight: 600,
                                color: "#2196f3",
                              }}
                            >
                              SHIFTS
                            </Typography>
                          </Box>
                        </TableCell>
                        {weekDays.map((day) => renderShiftsCell(day, currentView === "Day"))}
                      </TableRow>

                      {filteredEmployees.map((employee) => (
                        <TableRow
                          key={employee.id}
                          sx={{ height: currentView === "Day" ? 60 : "auto" }}
                        >
                          <TableCell
                            sx={{
                              backgroundColor: "#fafafa",
                              cursor: "move",
                              borderRight: "2px solid #e0e0e0",
                              width: currentView === "Day" ? "80px" : "140px",
                              minWidth: currentView === "Day" ? "80px" : "140px",
                              maxWidth: currentView === "Day" ? "80px" : "140px",
                              height: currentView === "Day" ? 60 : "auto",
                            }}
                            draggable
                            onDragStart={(e) => handleDragStart(e, employee)}
                            onDragEnd={() => {
                              setDraggedEmployee(null);
                              setDragOverShift(null);
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <PictureAvatar
                                image={getOriginalEmployeeData(employee.id)?.profileImg}
                                firstName={getOriginalEmployeeData(employee.id)?.firstName}
                                lastName={getOriginalEmployeeData(employee.id)?.lastName}
                                userId={employee.id}
                                size="md"
                                disableHover
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: currentView === "Day" ? "0.8rem" : "0.8rem",
                                  fontWeight: 400,
                                }}
                              >
                                {employee.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          {weekDays.map((day) =>
                            renderEmployeeCell(day, employee, currentView === "Day")
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Instructions */}
          <Box sx={{ mt: 3, p: 2, backgroundColor: "#f9f9f9", borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Instructions:
            </Typography>
            <Box component="ul" sx={{ fontSize: "0.8rem", margin: 0, paddingLeft: 2 }}>
              <li>
                <strong>Required:</strong> Drag employee names to specific shift cards to assign
                them
              </li>
              <li>Each shift card shows available time slots and assigned employees</li>
              <li>Hover effects indicate valid drop zones for assignments</li>
              <li>Click the X on employee chips to remove specific assignments</li>
              <li>Use the shift dropdown to filter assignments</li>
              <li>Switch between Day, Week, and Month views</li>
              <li>Hover over monthly summary cards to see employee details</li>
              <li>Each shift can have multiple employees assigned</li>
              <li>Employees can be assigned to multiple shifts per day</li>
              <li>
                <strong>Time Overlap Prevention:</strong> The system prevents assigning employees to
                overlapping shifts on the same day
              </li>
              <li>
                Shift cards turn red with &quot;TIME OVERLAP&quot; warning when dragging would cause
                conflicts
              </li>
              <li>Error messages appear when attempting to assign to overlapping shifts</li>
              <li>
                <strong>Position Assignment:</strong> Click the person icon on shift cards to assign
                specific positions to employees
              </li>
              <li>
                <strong>Clear Position:</strong> Use the &quot;Clear Position&quot; button in the position popup to remove
                assigned positions from employees
              </li>
              <li>
                <strong>Position Availability:</strong> The system prevents assigning employees to positions
                that have reached their maximum capacity for that specific date
              </li>
              <li>
                <strong>Position Conflicts:</strong> Two different employees cannot be assigned to the same
                position on the same day. The system shows specific error messages when conflicts occur.
              </li>
              <li>
                <strong>Position Limits:</strong> Each position has a maximum number of employees that can
                be assigned per day, shown as &quot;Available/Total&quot; in the position selection popup
              </li>
              <li>
                <strong>Multiple Day Assignments:</strong> The same employee can be assigned to the same
                position across multiple days without affecting availability limits
              </li>
              <li>
                <strong>Position Errors:</strong> Error messages appear when attempting to assign to
                positions that have reached their capacity limit or when conflicts with other employees occur
              </li>
            </Box>
          </Box>
        </DialogContent>

        {/* Position Selection Popup */}
        <Popover
          open={Boolean(positionPopupAnchor)}
          anchorEl={positionPopupAnchor}
          onClose={handlePositionClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              border: "1px solid #e0e0e0",
              backgroundColor: "#fff", // fix opacity
              opacity: 1, // fix opacity
              minWidth: 280,
              p: 0,
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Assign Position
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
              {selectedEmployeeForPosition?.name} - {selectedDateForPosition ? moment(selectedDateForPosition).format("MMM DD, YYYY") : ""}
            </Typography>
            
            <Autocomplete
              options={getPopoverPositions().map((pos) => pos.positionName)}
              value={getPopoverAssignedPosition()}
              onChange={(e, newValue) => {
                setSelectedPosition(newValue || "");
                handlePositionSelect(newValue);
              }}
              getOptionLabel={(option) => option || ""}
              renderOption={(props, option) => {
                const pos = getPopoverPositions().find((p) => p.positionName === option);
                const availability = getPositionAvailabilityForDate(
                  option, 
                  selectedDateForPosition, 
                  selectedShiftForPosition?.slug
                );
                const isAvailable = canAssignToPosition(
                  option, 
                  selectedDateForPosition, 
                  selectedShiftForPosition?.slug, 
                  selectedEmployeeForPosition?.id
                );
                const conflictCheck = checkPositionConflict(
                  option, 
                  selectedDateForPosition, 
                  selectedShiftForPosition?.slug, 
                  selectedEmployeeForPosition?.id
                );
                
                return (
                  <li {...props} key={option} style={{ 
                    opacity: isAvailable && !conflictCheck.hasConflict ? 1 : 0.5,
                    cursor: isAvailable && !conflictCheck.hasConflict ? 'pointer' : 'not-allowed'
                  }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600, color: isAvailable && !conflictCheck.hasConflict ? 'inherit' : '#999' }}>
                          {option}
                        </span>
                        <Chip
                          label={`${availability.available}/${availability.total}`}
                          size="small"
                          sx={{
                            fontSize: '0.7rem',
                            height: 20,
                            backgroundColor: availability.available > 0 ? '#e8f5e8' : '#ffebee',
                            color: availability.available > 0 ? '#2e7d32' : '#c62828',
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                      {pos && (
                        <span style={{ fontSize: '0.8em', color: '#888' }}>
                          Pay: ${pos.payRate} &nbsp;|&nbsp; Bill: ${pos.billRate}
                        </span>
                      )}
                      {conflictCheck.hasConflict && (
                        <span style={{ fontSize: '0.75em', color: '#f44336', fontWeight: 500 }}>
                          Position at maximum capacity ({availability.assigned}/{availability.total})
                        </span>
                      )}
                      {!isAvailable && availability.total > 0 && !conflictCheck.hasConflict && (
                        <span style={{ fontSize: '0.75em', color: '#f44336', fontWeight: 500 }}>
                          All positions filled ({availability.assigned}/{availability.total})
                        </span>
                      )}
                    </Box>
                  </li>
                );
              }}
              filterOptions={(options, { inputValue }) => {
                // Show all positions but mark unavailable ones as disabled
                return options;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Position"
                  size="small"
                  placeholder="Choose a position..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                    },
                  }}
                />
              )}
              sx={{ mb: 2 }}
              disableClearable
              fullWidth
            />
            
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              {getPopoverAssignedPosition() && (
                <Button
                  variant="text"
                  color="error"
                  size="small"
                  onClick={() => handlePositionSelect("")}
                  sx={{ mb: 0, textTransform: "none" }}
                  fullWidth
                >
                  Clear Position
                </Button>
              )}
              <Button
                size="small"
                onClick={handlePositionClose}
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Popover>

        <DialogActions sx={{ padding: 3, gap: 2 }}>
          <Button variant="contained" color="error" onClick={() => setIsModalOpen(false)}>
            CANCEL
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            sx={{ backgroundColor: "#1976d2" }}
            onClick={handleSaveChanges}
          >
            SAVE
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShiftCalendar;
